import { useEffect, useRef, useState } from 'react'
import { toggleMic, useMicState } from './micAudio'
import useWindowDimensions from '../../hooks/useWindowDimension'
import { SCENES, MIC_SCENES } from './sceneContent'

export default function AudioHUD() {
  const [scene, setScene] = useState(0)
  const [progress, setProgress] = useState(0)
  const [visible, setVisible] = useState(true)
  const [deepOpen, setDeepOpen] = useState(false)
  const lastScroll = useRef(0)
  const hideTimer = useRef(null)
  const { width } = useWindowDimensions()
  const isMobile = width < 640

  useEffect(() => {
    // drei's ScrollControls scrolls its own internal container, not the window.
    // Locate that container (the one overflowing element) and track its scrollTop.
    const findScroller = () =>
      [...document.querySelectorAll('div')].find((d) => {
        const s = getComputedStyle(d)
        return (s.overflowY === 'auto' || s.overflowY === 'scroll') &&
          d.scrollHeight > d.clientHeight + 1
      })

    let scroller = null
    let raf = 0

    const onScroll = () => {
      if (!scroller) return
      const maxScroll = scroller.scrollHeight - scroller.clientHeight || 1
      const raw = scroller.scrollTop / maxScroll
      setProgress(raw)
      setScene(Math.min(SCENES.length - 1, Math.floor(raw * SCENES.length)))
      setVisible(true)
      clearTimeout(hideTimer.current)
      hideTimer.current = setTimeout(() => setVisible(false), 2500)
    }

    const attach = () => {
      scroller = findScroller()
      if (scroller) {
        scroller.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
      } else {
        raf = requestAnimationFrame(attach)
      }
    }
    attach()

    return () => {
      cancelAnimationFrame(raf)
      scroller && scroller.removeEventListener('scroll', onScroll)
    }
  }, [])

  // Close the deep-dive popup whenever the scene changes.
  useEffect(() => { setDeepOpen(false) }, [scene])

  const current = SCENES[scene] || SCENES[0]
  const mic = useMicState()
  // Pulse the mic button on scenes where speaking changes the visuals.
  const micHint = MIC_SCENES.includes(scene) && !mic.active && !mic.pending

  return (
    <>
      {/* Live-mic toggle — drives the waveform & spectrogram scenes from your voice */}
      <button
        onClick={toggleMic}
        style={{
          position: 'fixed',
          bottom: isMobile ? 14 : 28,
          left: isMobile ? 14 : 28,
          zIndex: 100,
          pointerEvents: 'auto',
          cursor: 'pointer',
          fontFamily: 'monospace',
          fontSize: isMobile ? 9 : 11,
          letterSpacing: '0.08em',
          color: mic.active ? '#00ddff' : micHint ? '#aee3ff' : '#7fa8c0',
          padding: isMobile ? '6px 10px' : '8px 14px',
          borderRadius: 999,
          border: `1px solid ${mic.active || micHint ? 'rgba(0,221,255,0.6)' : 'rgba(80,170,230,0.3)'}`,
          background: mic.active ? 'rgba(0,180,230,0.18)' : 'rgba(20,40,60,0.45)',
          backdropFilter: 'blur(4px)',
          transition: 'all 0.25s ease',
          animation: micHint ? 'micPulse 1.8s ease-in-out infinite' : 'none',
        }}
      >
        {mic.pending ? '◌ ENABLING…' : mic.active ? '● LIVE MIC ON' : '○ USE MY MIC'}
        {mic.error && <span style={{ color: '#ff6688', marginLeft: 8 }}>{mic.error}</span>}
      </button>

      {/* Invitation that appears only when speaking would change this scene */}
      {micHint && (
        <div style={{
          position: 'fixed',
          bottom: isMobile ? 46 : 62,
          left: isMobile ? 16 : 30,
          zIndex: 100,
          pointerEvents: 'none',
          fontFamily: 'monospace',
          fontSize: isMobile ? 9 : 10,
          letterSpacing: '0.1em',
          color: '#5fb8e0',
          animation: 'hintFade 1.8s ease-in-out infinite',
        }}>
          ↓ speak to see your own voice
        </div>
      )}

      {/* Top-center: scene info — maxWidth leaves clearance for the progress
          dots fixed on the right edge so the two never overlap on phones. */}
      <div
        style={{
          position: 'fixed',
          top: isMobile ? 68 : 28,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          pointerEvents: 'none',
          transition: 'opacity 0.5s ease',
          opacity: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          width: isMobile ? 'calc(100vw - 140px)' : 'auto',
        }}
      >
        <div style={{
          fontSize: isMobile ? 9 : 10,
          letterSpacing: '0.2em',
          color: '#336688',
          textTransform: 'uppercase',
          marginBottom: 6,
          fontFamily: 'monospace',
        }}>
          {String(scene + 1).padStart(2, '0')} / {String(SCENES.length).padStart(2, '0')} — Audio AI
        </div>
        <div style={{
          fontSize: isMobile ? 16 : 22,
          fontWeight: 700,
          color: '#ffffff',
          letterSpacing: '-0.01em',
          lineHeight: 1.1,
          marginBottom: 6,
          fontFamily: 'system-ui, sans-serif',
        }}>
          {current.title}
        </div>
        <div style={{
          fontSize: isMobile ? 12 : 14,
          color: '#7fa8c0',
          fontFamily: 'system-ui, sans-serif',
          maxWidth: 280,
          marginBottom: isMobile ? 10 : 14,
        }}>
          {current.subtitle}
        </div>

        {/* Concept explanation */}
        <div style={{
          fontSize: isMobile ? 12.5 : 14.5,
          lineHeight: 1.55,
          color: '#9fb6c4',
          fontFamily: 'system-ui, sans-serif',
          maxWidth: isMobile ? '100%' : 640,
          marginBottom: isMobile ? 12 : 16,
        }}>
          {current.desc}
        </div>

        {/* Real-world audio AI applications */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxWidth: isMobile ? '100%' : 660, alignItems: 'center', justifyContent: 'center' }}>
          <span style={{
            fontSize: isMobile ? 8 : 9,
            letterSpacing: '0.18em',
            color: '#4a6680',
            textTransform: 'uppercase',
            fontFamily: 'monospace',
            marginRight: 2,
          }}>
            Used in
          </span>
          {current.apps.map((app) => (
            <span key={app} style={{
              fontSize: isMobile ? 9 : 10.5,
              color: '#aee3ff',
              fontFamily: 'monospace',
              padding: '3px 8px',
              borderRadius: 999,
              border: '1px solid rgba(80,170,230,0.3)',
              background: 'rgba(40,120,180,0.12)',
              whiteSpace: 'nowrap',
            }}>
              {app}
            </span>
          ))}
        </div>
      </div>

      {/* Top-right: "Going deeper" toggle */}
      {current.detail && (
        <button
          onClick={() => setDeepOpen((v) => !v)}
          style={{
            position: 'fixed',
            top: isMobile ? 14 : 30,
            right: isMobile ? 14 : 30,
            zIndex: 101,
            pointerEvents: 'auto',
            cursor: 'pointer',
            fontFamily: 'monospace',
            fontSize: isMobile ? 9 : 10,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: deepOpen ? '#aee3ff' : '#7fa8c0',
            padding: isMobile ? '6px 10px' : '7px 13px',
            borderRadius: 999,
            border: `1px solid ${deepOpen ? 'rgba(0,221,255,0.6)' : 'rgba(80,170,230,0.3)'}`,
            background: deepOpen ? 'rgba(0,180,230,0.18)' : 'rgba(20,40,60,0.45)',
            backdropFilter: 'blur(4px)',
            transition: 'all 0.25s ease',
          }}
        >
          {deepOpen ? '✕ Close' : isMobile ? 'ⓘ Info' : 'ⓘ Going deeper'}
        </button>
      )}

      {/* Deep-dive popup — fixed px width overflowed narrow phones, so on
          mobile it's pinned to both edges instead of a fixed width. */}
      {deepOpen && current.detail && (
        <div style={{
          position: 'fixed',
          top: isMobile ? 56 : 74,
          right: isMobile ? 12 : 30,
          left: isMobile ? 12 : 'auto',
          width: isMobile ? 'auto' : 320,
          zIndex: 101,
          pointerEvents: 'auto',
          padding: isMobile ? '14px 16px' : '16px 18px',
          borderRadius: 12,
          border: '1px solid rgba(80,170,230,0.28)',
          background: 'rgba(10,22,36,0.92)',
          backdropFilter: 'blur(8px)',
          boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
        }}>
          <div style={{
            color: '#46708f',
            fontFamily: 'monospace',
            fontSize: 9,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            marginBottom: 8,
          }}>
            Going deeper — {current.title}
          </div>
          <div style={{
            fontSize: isMobile ? 13 : 14.5,
            lineHeight: 1.6,
            color: '#bcd0de',
            fontFamily: 'system-ui, sans-serif',
          }}>
            {current.detail}
          </div>
        </div>
      )}

      {/* Right: vertical progress bar */}
      <div style={{
        position: 'fixed',
        right: isMobile ? 10 : 24,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? 6 : 8,
        alignItems: 'center',
        pointerEvents: 'none',
        transition: 'opacity 0.5s ease',
        opacity: visible ? 1 : 0.3,
      }}>
        {SCENES.map((s, i) => (
          <div
            key={i}
            title={s.title}
            style={{
              width: i === scene ? 8 : 3,
              height: i === scene ? 8 : 3,
              borderRadius: '50%',
              background: i === scene ? '#00ddff' : i < scene ? '#334455' : '#1a2233',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>

      {/* Bottom: scroll hint (only at start) */}
      {progress < 0.04 && (
        <div style={{
          position: 'fixed',
          bottom: 32,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 100,
          textAlign: 'center',
          pointerEvents: 'none',
          animation: 'bounce 2s infinite',
        }}>
          <div style={{ color: '#334455', fontSize: 11, letterSpacing: '0.2em', fontFamily: 'monospace', textTransform: 'uppercase' }}>
            Scroll to explore
          </div>
          <div style={{ color: '#22aaff', fontSize: 20, marginTop: 6 }}>↓</div>
        </div>
      )}

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(6px); }
        }
        @keyframes micPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,221,255,0.0); transform: scale(1); }
          50% { box-shadow: 0 0 16px 3px rgba(0,221,255,0.45); transform: scale(1.05); }
        }
        @keyframes hintFade {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 0.9; }
        }
      `}</style>
    </>
  )
}
